import { authProvider } from '@crest/web-common/api/auth-provider.js';
import { dataProvider } from '@crest/web-common/api/data-provider.js';
import { AppUpdatePrompt } from '@crest/web-common/components/AppUpdatePrompt.js';
import { AppLayout } from '@crest/web-common/layout/AppLayout.js';
import { HomePage } from '@crest/web-common/pages/HomePage.js';
import { LoginPage } from '@crest/web-common/pages/LoginPage.js';
import { AccessCenterPage } from '@crest/web-common/pages/admin/AccessCenterPage.js';
import { AdminUsersPage } from '@crest/web-common/pages/admin/AdminUsersPage.js';
import { AppCenterPage } from '@crest/web-common/pages/admin/AppCenterPage.js';
import { AuditLogPage } from '@crest/web-common/pages/admin/AuditLogPage.js';
import { BuTreePage } from '@crest/web-common/pages/admin/BuTreePage.js';
import { LoginLogPage } from '@crest/web-common/pages/admin/LoginLogPage.js';
import { PlatformMailConfigPage } from '@crest/web-common/pages/admin/NotifyHistoryPage.js';
import { ReleaseRolloutPage } from '@crest/web-common/pages/admin/ReleaseRolloutPage.js';
import {
  SipCallAssignmentPage,
  SipCallGlobalSettingsPage,
} from '@crest/web-common/pages/admin/SipDialerSettingsPage.js';
import { SystemConfigPage } from '@crest/web-common/pages/admin/SystemConfigPage.js';
import { UpdateLogPage } from '@crest/web-common/pages/admin/UpdateLogPage.js';
import { UserFeedbackPage } from '@crest/web-common/pages/admin/UserFeedbackPage.js';
import { crestThemeFor } from '@crest/web-common/theme/antd-theme.js';
import {
  applyCrestThemeToElement,
  useCrestTheme,
} from '@crest/web-common/theme/crest-theme-preference.js';
import { useNotificationProvider } from '@refinedev/antd';
import { Authenticated, Refine } from '@refinedev/core';
import routerProvider, { CatchAllNavigate } from '@refinedev/react-router-v6';
import { App as AntdApp, ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import zhCN from 'antd/locale/zh_CN';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { MicroFrontendOutlet } from './MicroFrontendOutlet.js';
import { PersonalWorkspace } from './PersonalWorkspace.js';
import { PortalAssistantButton, PortalAssistantProvider } from './PortalAssistant.js';
import { loadPlatformSitemap } from './app-runtime-context.js';
import {
  isDelegatedMicroAppPath,
  isNavigationPathPublished,
  isPortalAppPublished,
} from './micro-apps.js';

export function PlatformApp() {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? enUS : zhCN;
  const theme = useCrestTheme();
  useEffect(() => applyCrestThemeToElement(theme), [theme]);

  return (
    <BrowserRouter>
      <ConfigProvider locale={locale} theme={crestThemeFor(theme)}>
        <AntdApp>
          <AppUpdatePrompt />
          <Refine
            dataProvider={dataProvider}
            authProvider={authProvider}
            notificationProvider={useNotificationProvider}
            routerProvider={routerProvider}
            resources={[]}
            options={{ syncWithLocation: true, warnWhenUnsavedChanges: true }}
          >
            <Routes>
              <Route path="/work/operations/*" element={<PersonalWorkspace />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/email-code" element={<LoginPage mode="email-code" />} />
              <Route
                path="/platform"
                element={
                  <LoginPage
                    mode="password"
                    loginPath="/platform"
                    passwordRedirectTo="/app/admin/app-center"
                  />
                }
              />
              <Route
                path="/crm"
                element={
                  <LoginPage
                    mode="password"
                    loginPath="/crm"
                    variant="crm"
                    passwordRedirectTo="/app/crm"
                  />
                }
              />
              <Route
                path="/agent"
                element={
                  <LoginPage
                    mode="password"
                    loginPath="/agent"
                    variant="agent"
                    passwordRedirectTo="/app/agent/workbench"
                  />
                }
              />
              <Route
                path="/operate"
                element={
                  <LoginPage
                    mode="password"
                    loginPath="/operate"
                    variant="operations"
                    passwordRedirectTo="/app/operations"
                  />
                }
              />
              <Route path="/crest" element={<LoginPage mode="password" />} />
              <Route path="/login/dingtalk/callback" element={<LoginPage />} />
              <Route
                element={
                  <Authenticated key="platform-auth-required" fallback={<CatchAllNavigate to="/login" />}>
                    <PortalAssistantProvider>
                      <Outlet />
                    </PortalAssistantProvider>
                  </Authenticated>
                }
              >
                <Route path="/" element={<Navigate to="/app/admin/app-center" replace />} />
                <Route
                  path="/app/admin"
                  element={
                    <AppLayout
                      headerActions={<PortalAssistantButton />}
                      appNameOverride="admin"
                      responsiveSider
                      isNavigationPathAvailable={isNavigationPathPublished}
                      isPortalAppAvailable={isPortalAppPublished}
                      isDelegatedAdminPath={isDelegatedMicroAppPath}
                      sitemapLoader={loadPlatformSitemap}
                    />
                  }
                >
                  <Route index element={<HomePage />} />
                  <Route path="app-center" element={<AppCenterPage />} />
                  <Route path="bu-tree" element={<BuTreePage />} />
                  <Route path="users" element={<AdminUsersPage />} />
                  <Route path="user-feedback" element={<UserFeedbackPage />} />
                  <Route path="access-center" element={<AccessCenterPage />} />
                  <Route
                    path="platform-permissions"
                    element={<Navigate to="/app/admin/access-center?tab=platform-roles" replace />}
                  />
                  <Route path="release-rollouts" element={<ReleaseRolloutPage />} />
                  <Route path="login-logs" element={<LoginLogPage />} />
                  <Route path="update-log" element={<UpdateLogPage />} />
                  <Route path="audit-log" element={<AuditLogPage />} />
                  <Route path="notify-history" element={<PlatformMailConfigPage />} />
                  <Route path="call-config/settings" element={<SipCallGlobalSettingsPage />} />
                  <Route path="call-config/assignments" element={<SipCallAssignmentPage />} />
                  <Route path="system-config" element={<SystemConfigPage />} />
                  <Route path="settings" element={<Navigate to="../system-config" replace />} />
                  <Route
                    path="integration/logs"
                    element={
                      <Navigate to="/app/crm/settings?section=integration-logs" replace />
                    }
                  />
                  <Route path="*" element={<MicroFrontendOutlet />} />
                </Route>
                <Route
                  path="/app/:appName"
                  element={
                    <AppLayout
                      headerActions={<PortalAssistantButton />}
                      responsiveSider
                      isNavigationPathAvailable={isNavigationPathPublished}
                      isPortalAppAvailable={isPortalAppPublished}
                      isDelegatedAdminPath={isDelegatedMicroAppPath}
                      sitemapLoader={loadPlatformSitemap}
                    />
                  }
                >
                  <Route index element={<MicroFrontendOutlet />} />
                  <Route path="*" element={<MicroFrontendOutlet />} />
                </Route>
              </Route>
            </Routes>
          </Refine>
        </AntdApp>
      </ConfigProvider>
    </BrowserRouter>
  );
}
